
import { useState, useEffect } from 'react';
import { supabase } from '@/integrations/supabase/client';
import { PostTreatmentReport } from '@/types/supabase';

// Define the report status type
export type PostTreatmentStatus = 'none' | 'in-progress' | 'completed' | 'locked';

// This hook helps check the status of post-treatment reports for a patient
export function usePostTreatmentStatus() {
  const [patientReports, setPatientReports] = useState<Record<string, PostTreatmentStatus>>({});
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);

  // Check if post-treatment report exists for a patient
  // This is now a synchronous function that uses cached values when available
  const checkPostTreatmentExists = (patientId: string): PostTreatmentStatus => {
    // Return cached result if available
    if (patientReports[patientId]) {
      return patientReports[patientId];
    }
    
    // If not cached, trigger the fetch but return 'none' for now
    // The component will re-render when the cache is updated
    fetchPatientStatus(patientId);
    return 'none';
  };
  
  // Separate async function to fetch status
  const fetchPatientStatus = async (patientId: string) => {
    try {
      setIsLoading(true);
      
      const { data, error } = await supabase
        .from('post_treatment_reports')
        .select('status')
        .eq('patient_id', patientId)
        .maybeSingle();
      
      if (error) {
        throw error;
      }
      
      let status: PostTreatmentStatus = 'none';
      
      if (data) {
        status = (data as PostTreatmentReport).status as PostTreatmentStatus || 'none';
      }
      
      // Cache the result
      setPatientReports(prev => ({
        ...prev,
        [patientId]: status
      }));
      
    } catch (err: any) {
      console.error('Error checking post-treatment status:', err);
      setError(err.message || 'Failed to check post-treatment status');
    } finally {
      setIsLoading(false);
    }
  };

  // Update a patient's report status
  const updateReportStatus = async (patientId: string, status: PostTreatmentStatus) => {
    try {
      setIsLoading(true);
      
      // Check if report exists first
      const { data: existingReport, error: checkError } = await supabase
        .from('post_treatment_reports')
        .select('id')
        .eq('patient_id', patientId)
        .maybeSingle();
      
      if (checkError) {
        throw checkError;
      }
      
      if (existingReport) {
        // Update existing report
        const { error: updateError } = await supabase
          .from('post_treatment_reports')
          .update({ status })
          .eq('id', existingReport.id);
        
        if (updateError) {
          throw updateError;
        }
      } else if (status !== 'none') {
        // Create new report if needed and status is not 'none'
        const { error: insertError } = await supabase
          .from('post_treatment_reports')
          .insert({
            patient_id: patientId,
            // These would need to be filled with real values
            doctor_id: 'placeholder', // This needs to be updated with a real doctor ID
            treatment_summary: 'Initial report',
            status
          });
        
        if (insertError) {
          throw insertError;
        }
      }
      
      // Update cache
      setPatientReports(prev => ({
        ...prev,
        [patientId]: status
      }));
      
    } catch (err: any) {
      console.error('Error updating report status:', err);
      setError(err.message || 'Failed to update report status');
    } finally {
      setIsLoading(false);
    }
  };

  return {
    patientReports,
    isLoading,
    error,
    checkPostTreatmentExists,
    updateReportStatus
  };
}
